import { useCallback } from 'react';

import { useAuth } from '@/store/auth/auth.store';
import { useDashboardSettingsQuery, useSaveDashboardSettingsMutation } from '@/store/dashboard';
import type { UnitPreferences } from '@/utils/dashboard.types';
import type { CurrencyUnit, LengthUnit, VolumeUnit } from '@/utils/unitConversion';

const DEFAULT_LENGTH: LengthUnit = 'ft';
const DEFAULT_VOLUME: VolumeUnit = 'gal';
const DEFAULT_CURRENCY: CurrencyUnit = '$';

export function useUnitPreferences() {
  const userId = useAuth((s) => s.session?.id);
  const { data: settings, isSuccess } = useDashboardSettingsQuery(userId);
  const { mutate: save } = useSaveDashboardSettingsMutation(userId);

  const lengthUnit: LengthUnit = settings?.unitPreferences?.length ?? DEFAULT_LENGTH;
  const volumeUnit: VolumeUnit = settings?.unitPreferences?.volume ?? DEFAULT_VOLUME;
  const currencyUnit: CurrencyUnit = settings?.unitPreferences?.currency ?? DEFAULT_CURRENCY;

  // Until the settings blob has actually loaded, the units above are only the
  // defaults. Callers must not persist a preference yet (a save would clobber
  // the not-yet-loaded dashboards), so the unit switch stays disabled.
  const isLoaded = isSuccess;

  // Writes one unit and keeps the others as stored (or their defaults).
  const saveUnit = useCallback(
    (patch: Partial<UnitPreferences>) => {
      if (!isSuccess) return;
      const base = settings ?? { dashboards: {} };
      save({
        ...base,
        unitPreferences: {
          length: base.unitPreferences?.length ?? DEFAULT_LENGTH,
          volume: base.unitPreferences?.volume ?? DEFAULT_VOLUME,
          currency: base.unitPreferences?.currency ?? DEFAULT_CURRENCY,
          ...patch,
        },
      });
    },
    [isSuccess, settings, save],
  );

  const setLengthUnit = useCallback((unit: LengthUnit) => saveUnit({ length: unit }), [saveUnit]);

  const setVolumeUnit = useCallback((unit: VolumeUnit) => saveUnit({ volume: unit }), [saveUnit]);

  const setCurrencyUnit = useCallback(
    (unit: CurrencyUnit) => saveUnit({ currency: unit }),
    [saveUnit],
  );

  return {
    lengthUnit,
    volumeUnit,
    currencyUnit,
    isLoaded,
    setLengthUnit,
    setVolumeUnit,
    setCurrencyUnit,
  };
}
